import { X, Calendar as CalendarIcon, Search, Square, CheckSquare, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Table } from "@tanstack/react-table";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Calendar } from "components/ui/calendar";
import { cn } from "lib/utils";
import { Combobox } from "components/ui/combobox";
import { AlertCardComponent, AlertCard, AlertBucket, AlertFilter } from "./alert-card";
import { OrgFilterBar } from "./org-filter-bar";
import { activeFilterClass } from "./filter-styles";
import { OrgLevelKey, OrgLevelOptions } from "pages/shipments/api/shipments";
import { useLocation } from "react-router-dom";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export interface ComboboxOption {
  value: string
  label: string
}
export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  buttonClassName?: string
}

interface DateRange {
  from?: Date;
  to?: Date;
}

interface CustomerOption {
  cust_code: string;
  cust_name: string;
}

export interface OrgFilterControls {
  levels: OrgLevelOptions[];
  isActive: boolean;
  setLevel: (key: OrgLevelKey, value: string | undefined) => void;
  clear: () => void;
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  onDateRangeChange?: (dateRange: DateRange) => void;
  dateRange?: DateRange;
  alertCards: AlertCard[];
  activeAlertFilters: AlertFilter;
  onAlertFilterChange: (filters: AlertFilter) => void;
  customerOptions: CustomerOption[];
  selectedCustId?: string;
  onCustomerChange: (code: string | undefined) => void;
  orgFilter?: OrgFilterControls;
  stockStatusOptions?: { label: string; value: string }[];
  selectedStockStatus?: string;
  onStockStatusChange?: (value: string | undefined) => void;
  stockLocationOptions?: { label: string; value: string }[];
  selectedStockLocation?: string;
  onStockLocationChange?: (value: string | undefined) => void;
  isLoadingAlerts: boolean;
  shipmentStatus?: string;
  shipmentStatusCounts?: Record<string, number>;
  onShipmentStatusChange?: (value: string | undefined) => void;
  loadType?: string;
  loadTypeCounts?: Record<string, number>;
  onLoadTypeChange?: (value: string | undefined) => void;
  geocodingErrorsCount?: number;
  onGeocodingErrorsClick?: () => void;
}
// const TOP_FILTERS = [
//   { label: "Selected", count: 487, id: 'selected' },
//   { label: "Delayed", count: 118, id: 'delayed' },
//   { label: "Starred", count: 0, id: 'starred' },
//   { label: "EWB Expired", count: 0, id: 'ewb_expired' },
//   { label: "Untracked", count: 4, id: 'untracked' },
//   { label: "Round Trips", count: 2, id: 'round_trips' },
// ];


/**
 * Helper to normalize values for comparison, treating undefined, null, and empty string as equivalent
 * when checking for the absence of a filter.
 */
const areValuesEqual = (val1: any, val2: any): boolean => {
  const normalize = (val: any) => (val === undefined || val === null || val === '') ? undefined : val;
  return normalize(val1) === normalize(val2);
};

// Shipment statuses offered in the status dropdown; the Action Required card
// buckets select the same values
// Grouped by colour: green, yellow, purple, orange (parked), red
const SHIPMENT_STATUS_OPTIONS = [
  { value: "on_schedule", label: "On Schedule", dot: "bg-green-500" },
  { value: "running_late", label: "Running Late", dot: "bg-yellow-400" },
  { value: "update_status", label: "Update Status", dot: "bg-violet-500" },
  { value: "on_hold", label: "On Hold", dot: "bg-orange-400" },
  { value: "awaiting_pickup", label: "Awaiting Pickup", dot: "bg-orange-400" },
  { value: "awaiting_unloading", label: "Awaiting Unloading", dot: "bg-orange-400" },
  { value: "delayed", label: "Delayed", dot: "bg-red-600" },
  { value: "incident_reported", label: "Incident Reported", dot: "bg-red-600" },
];

// Values are ops_cn.load_type; PTL is stored as "P- Part Load"
const LOAD_TYPE_OPTIONS = [
  { value: "FTL", label: "FTL" },
  { value: "P- Part Load", label: "PTL" },
  { value: "ODC", label: "ODC" },
];


export function DataTableToolbar<TData>({
  table,
  onDateRangeChange,
  dateRange,
  alertCards,
  activeAlertFilters,
  onAlertFilterChange,
  customerOptions,
  selectedCustId,
  onCustomerChange,
  orgFilter,
  stockStatusOptions,
  selectedStockStatus,
  onStockStatusChange,
  stockLocationOptions,
  selectedStockLocation,
  onStockLocationChange,
  isLoadingAlerts,
  shipmentStatus,
  shipmentStatusCounts,
  onShipmentStatusChange = () => { },
  loadType,
  loadTypeCounts,
  onLoadTypeChange,
  geocodingErrorsCount = 0,
  onGeocodingErrorsClick = () => { },
}: DataTableToolbarProps<TData>) {
  const location = useLocation();
  const isInTransitPage = location.pathname.toLowerCase().includes("in-transit");
  const noAlertCards = location.pathname.toLowerCase().includes("local") || location.pathname.toLowerCase().includes("dmv");
  const isInStockPage = location.pathname.toLowerCase().includes("in-stock");

  const [date, setDate] = useState<DateRange>(dateRange || { from: undefined, to: undefined });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null); // Temporary storage for from date


  const [searchQuery, setSearchQuery] = useState(table.getState().globalFilter ?? "");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  React.useEffect(() => {
    if (table.getState().globalFilter !== debouncedSearchQuery) {
      table.setGlobalFilter(debouncedSearchQuery || "");
    }
  }, [debouncedSearchQuery, table]);

  // State to manage the multi-select top filters (e.g., Selected, Delayed)
  // const [selectedTopFilters, setSelectedTopFilters] = useState<string[]>(['selected']); 
  // Names of customers seen so far, so a selected customer that drops out of the current
  // options (e.g. after a search) still shows its name instead of "All Customers"
  const customerNames = React.useRef(new Map<string, string>());
  customerOptions.forEach((c) => {
    if (c.cust_code) customerNames.current.set(c.cust_code, c.cust_name);
  });

  const customerComboboxOptions: ComboboxOption[] = [
    { value: '', label: 'All Customers' },
    ...customerOptions
      .filter(c => c.cust_code && c.cust_code !== '')
      .map(c => ({ value: c.cust_code, label: c.cust_name }))
  ];
  if (selectedCustId && !customerOptions.some((c) => c.cust_code === selectedCustId)) {
    customerComboboxOptions.push({
      value: selectedCustId,
      label: customerNames.current.get(selectedCustId) ?? selectedCustId,
    });
  }

  const areAlertsFiltered = Object.keys(activeAlertFilters).length > 0;
  const isFiltered = searchQuery.length > 0 || (date?.from || date?.to) || !!selectedCustId || !!selectedStockStatus || !!selectedStockLocation || areAlertsFiltered || !!shipmentStatus || !!loadType || !!orgFilter?.isActive;

  React.useEffect(() => {
    setDate(dateRange || { from: undefined, to: undefined });
  }, [dateRange]);

  const handleDateSelect = (selectedDate: Date | DateRange | undefined) => {
    if (!selectedDate) return;

    // Handle date selection logic
    if (selectedDate instanceof Date) {
      if (!tempFromDate) {
        setTempFromDate(selectedDate);
        setDate({ from: selectedDate, to: undefined });
      } else {
        const newRange = {
          from: tempFromDate,
          to: selectedDate >= tempFromDate ? selectedDate : tempFromDate,
        };
        setDate(newRange);
        setTempFromDate(null);
        if (onDateRangeChange) {
          onDateRangeChange(newRange);
        }
        setIsDatePickerOpen(false);
      }
    }
  };

  // const handleTopFilterClick = (id: string) => {
  //   setSelectedTopFilters(prev => {
  //     if (prev.includes(id)) {
  //       return prev.filter(filterId => filterId !== id);
  //     } else {
  //       return [...prev, id];
  //     }
  //   });
  //   console.log(`Toggled top filter: ${id}`);
  // };

  const handleCustomerChange = (code: string) => {
    const custId = code === '' ? undefined : code;

    onCustomerChange(custId);
  };

  const clearFilters = () => {
    table.setGlobalFilter("");
    setSearchQuery("");
    setDate({ from: undefined, to: undefined });
    setTempFromDate(null);
    onCustomerChange(undefined);
    if (onStockStatusChange) onStockStatusChange(undefined);
    if (onStockLocationChange) onStockLocationChange(undefined);
    onShipmentStatusChange(undefined);
    onLoadTypeChange?.(undefined);
    orgFilter?.clear();
    if (onDateRangeChange) {
      onDateRangeChange({ from: undefined, to: undefined });
    }
    onAlertFilterChange({});
  };
  /**
   * A card/bucket is active only when every filter it sets is currently applied.
   * shipment_status is owned by the status dropdown, so it is compared against that.
   */
  const isBucketActive = (filters: AlertFilter): boolean => {
    const entries = Object.entries(filters).filter(([, value]) => !areValuesEqual(value, undefined));
    if (entries.length === 0) return false;
    return entries.every(([key, value]) =>
      key === 'shipment_status'
        ? areValuesEqual(value, shipmentStatus)
        : areValuesEqual(value, activeAlertFilters[key as keyof AlertFilter])
    );
  };

  // Toggle: clicking the active card/bucket clears it, otherwise applies it
  const applyFilters = (filters: AlertFilter) => {
    const isCurrentlyActive = isBucketActive(filters);
    const { shipment_status, ...alertFilters } = filters;

    if (shipment_status) {
      onShipmentStatusChange(isCurrentlyActive ? undefined : shipment_status);
    }
    if (Object.keys(alertFilters).length > 0) {
      onAlertFilterChange(isCurrentlyActive ? {} : alertFilters);
    }
  };

  const handleCardClick = (card: AlertCard) => {
    if (card.type === 'single' && card.filters) {
      applyFilters(card.filters);
    }
  };

  const handleBucketClick = (bucket: AlertBucket) => {
    applyFilters(bucket.filters);
  };

  return (
    <div className="flex flex-col space-y-1">
      {/* <div className="flex space-x-3 overflow-x-auto pb-3">
        {TOP_FILTERS.map(filter => {
          const isActive = selectedTopFilters.includes(filter.id);
          const Icon = isActive ? CheckSquare : Square;

          return (
            <div
              key={filter.id}
              className={cn(
                  "flex-shrink-0 cursor-pointer h-8 px-3 py-1.5 text-sm transition-colors duration-150 border rounded-lg",
                  "flex items-center space-x-2 whitespace-nowrap",
                  isActive
                      ? "bg-amber-100 border-amber-400 text-gray-900 font-medium hover:bg-amber-200" 
                      : "bg-white border-gray-300 text-gray-600 font-normal hover:bg-gray-50"
              )}
              onClick={() => handleTopFilterClick(filter.id)}
            >
              <Icon 
                className={cn("w-4 h-4", isActive ? "text-amber-600 fill-amber-300" : "text-gray-500")} 
              />
              <span className="font-normal">{filter.label}</span>
              <span className={cn(
                  "font-semibold",
                  isActive ? "text-amber-800" : "text-gray-800"
              )}>
                  {filter.count}
              </span>
            </div>
          );
        })}
      </div> */}

      {orgFilter && (
        <OrgFilterBar levels={orgFilter.levels} onChange={orgFilter.setLevel} className="pb-4" />
      )}

      <div className={`flex gap-4 ${noAlertCards ? 'pb-0' : 'pb-4'}`}>
        {alertCards.map((card) => (
          <AlertCardComponent
            key={card.id}
            card={card}
            isActive={Boolean(card.filters && isBucketActive(card.filters))}
            onCardClick={handleCardClick}
            onBucketClick={handleBucketClick}
            isBucketActive={isBucketActive}
            loading={isLoadingAlerts}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex flex-1 items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search All Trips"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-8 w-[120px] lg:w-[180px] pl-8 text-xs font-normal border-gray-300 bg-white text-gray-800 shadow-sm placeholder:text-gray-500 hover:border-gray-400"
            />
          </div>

          <div className="h-8 w-[120px] lg:w-[160px] flex-shrink-0">
            <Combobox
              options={customerComboboxOptions}
              value={selectedCustId || ''}
              onValueChange={handleCustomerChange}
              placeholder="All Customers"
              searchPlaceholder="Search customers..."
              emptyText="No customer found."
              buttonClassName={cn("h-8 text-xs font-normal", selectedCustId && activeFilterClass)}
            />
          </div>
          {!isInStockPage && (
            <div className="flex items-center rounded-md border border-gray-300 bg-white shadow-sm">
              <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <div className="flex divide-x">
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      onClick={() => setIsDatePickerOpen(true)}
                      className={cn(
                        "h-7 px-2 py-1 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs",
                        !date?.from && "text-gray-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs font-normal text-gray-800">From Date</span>
                        {date?.from && <span className="text-xs truncate">{format(date.from, "dd MMM yyyy")}</span>}
                      </div>
                    </Button>
                  </PopoverTrigger>

                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      onClick={() => setIsDatePickerOpen(true)}
                      className={cn(
                        "h-7 px-2 py-1 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs",
                        !date?.to && "text-gray-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs font-normal text-gray-800">To Date</span>
                        {date?.to && <span className="text-xs truncate">{format(date.to, "dd MMM yyyy")}</span>}
                      </div>
                    </Button>
                  </PopoverTrigger>
                </div>

                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-4">
                    <div className="text-sm font-semibold mb-3 text-gray-900">
                      {!tempFromDate ? "Select start date" : "Select end date"}
                    </div>
                    <Calendar
                      mode="single"
                      selected={tempFromDate || date?.from}
                      onSelect={handleDateSelect}
                      numberOfMonths={2}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (tempFromDate) {
                          return date < tempFromDate;
                        }
                        return false;
                      }}
                    />
                    <div className="flex justify-end mt-4 pt-3 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsDatePickerOpen(false);
                          setTempFromDate(null);
                        }}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {stockStatusOptions && stockStatusOptions.length > 0 && onStockStatusChange && (
            <Select
              value={selectedStockStatus || "all"}
              onValueChange={(val) =>
                onStockStatusChange?.(val === "all" ? undefined : val)
              }
            >
              <SelectTrigger className={cn("h-8 w-[160px] text-xs font-normal", selectedStockStatus && activeFilterClass)}>
                <SelectValue placeholder="Stock Status" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all" className="text-xs">All Stock Status</SelectItem>
                {stockStatusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {stockLocationOptions && stockLocationOptions.length > 0 && onStockLocationChange && (
            <Select
              value={selectedStockLocation || "all"}
              onValueChange={(val) =>
                onStockLocationChange?.(val === "all" ? undefined : val)
              }
            >
              <SelectTrigger className={cn("h-8 w-[180px] text-xs font-normal", selectedStockLocation && activeFilterClass)}>
                <SelectValue placeholder="Stock Location" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all" className="text-xs">All Stock Locations</SelectItem>
                {stockLocationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Shipment Status Filter */}
          {isInTransitPage && (
            <Select
              value={shipmentStatus ?? "all"}
              onValueChange={(val) => onShipmentStatusChange(val === "all" ? undefined : val)}
            >
              <SelectTrigger
                className={cn(
                  "h-8 w-[180px] text-xs font-normal focus:ring-0 focus:ring-offset-0",
                  shipmentStatus && activeFilterClass
                )}
              >
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
                {SHIPMENT_STATUS_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    className="text-xs"
                    suffix={shipmentStatusCounts?.[opt.value]}
                  >
                    <span className="flex items-center gap-2">
                      <span className={cn("h-2 w-2 shrink-0 rounded-full", opt.dot)} />
                      {opt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Load Type Filter */}
          {isInTransitPage && onLoadTypeChange && (
            <Select
              value={loadType ?? "all"}
              onValueChange={(val) => onLoadTypeChange(val === "all" ? undefined : val)}
            >
              <SelectTrigger
                className={cn(
                  "h-8 w-[148px] text-xs font-normal focus:ring-0 focus:ring-offset-0",
                  loadType && activeFilterClass
                )}
              >
                <SelectValue placeholder="All Load Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Load Types</SelectItem>
                {LOAD_TYPE_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    className="text-xs"
                    suffix={loadTypeCounts?.[opt.value]}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Geocoding Errors Button */}
          {isInTransitPage && (
            <Button
              variant="outline"
              onClick={onGeocodingErrorsClick}
              className={cn(
                "h-8 px-3 text-xs",
                geocodingErrorsCount > 0 ? "text-red-600 border-red-200 hover:bg-red-50" : "border-gray-300 text-gray-800 shadow-sm"
              )}
            >
              <MapPin className="mr-2 h-3 w-3" />
              Geocoding Errors
              {geocodingErrorsCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                  {geocodingErrorsCount}
                </span>
              )}
            </Button>
          )}

          {(isFiltered) && (
            <Button variant="ghost" onClick={clearFilters} className="h-8 px-3">
              Reset filter
              <X className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
